import { Component, OnInit, ViewChild, AfterViewInit } from "@angular/core";
import { IPicklistFieldsData } from "../../interfaces/administration/picklistFieldsData";
import { MatTableDataSource } from "@angular/material/table";
import { PicklistService } from "../../services/administration/picklist/picklist.service";
import {
  IPicklistsValuesCreateData,
  IPicklistValuesData,
} from "../../interfaces/administration/picklistValuesData";
import { LoggedUserService } from "../../services/common/logged-user.service";
import { ILoggedUserData } from "../../interfaces/common/loggedUserData";
import { MatSort } from "@angular/material/sort";

@Component({
  selector: "picklists-component",
  templateUrl: "./picklists.component.html",
  styleUrls: ["./picklists.component.css"],
})
export class PickListsComponent implements OnInit, AfterViewInit {
  title = "Pick lists";
  isLoading = true;
  isLoadingValues = false;

  fieldsColDef = ["name", "action"];
  valuesColDef = ["value", "action"];
  selectedField!: IPicklistFieldsData;
  selectedValue!: IPicklistValuesData;

  fieldsData: MatTableDataSource<IPicklistFieldsData> = new MatTableDataSource(
    []
  );
  valuesData: MatTableDataSource<IPicklistValuesData> = new MatTableDataSource(
    []
  );

  showAddFieldForm = false;
  showEditFieldForm = false;

  showAddValueForm = false;
  showEditValueForm = false;
  currentUser: ILoggedUserData;

  @ViewChild("fieldsSort") fieldsSort!: MatSort;
  @ViewChild("valuesSort") valuesSort!: MatSort;

  constructor(
    private picklistService: PicklistService,
    private loggedUserService: LoggedUserService
  ) {}

  ngOnInit(): void {
    this.loggedUserService.getLoggedUserData().subscribe((data) => {
      this.currentUser = data;
      this.loadFieldsData();
    });
  }

  ngAfterViewInit(): void {
    // Set default sorting configuration for both tables
    if (this.fieldsSort) {
      this.fieldsSort.active = "name";
      this.fieldsSort.direction = "asc";

      if (this.fieldsData) {
        this.fieldsData.sort = this.fieldsSort;
      }
    }

    if (this.valuesSort) {
      this.valuesSort.active = "value";
      this.valuesSort.direction = "asc";

      if (this.valuesData) {
        this.valuesData.sort = this.valuesSort;
      }
    }
  }

  loadFieldsData(): void {
    this.picklistService.getAllPicklistsFields().subscribe((data) => {
      this.fieldsData = new MatTableDataSource(data);

      // Apply sorting with default settings
      if (this.fieldsSort) {
        this.fieldsData.sort = this.fieldsSort;

        // If fieldsSort has not been initialized yet, set defaults
        if (!this.fieldsSort.active) {
          this.fieldsSort.active = "name";
          this.fieldsSort.direction = "asc";
        }
      }

      this.isLoading = false;
    });
  }

  onFieldRowSelected(field: IPicklistFieldsData) {
    // Close any open edit forms when changing fields
    this.showEditValueForm = false;
    this.showAddValueForm = false;
    this.selectedValue = undefined as any;

    this.isLoadingValues = true;
    this.picklistService.getPicklistValues(field.id).subscribe((data) => {
      this.selectedField = field;
      this.valuesData = new MatTableDataSource(data);

      // Apply sorting with default settings
      if (this.valuesSort) {
        this.valuesData.sort = this.valuesSort;

        // If valuesSort has not been initialized yet, set defaults
        if (!this.valuesSort.active) {
          this.valuesSort.active = "value";
          this.valuesSort.direction = "asc";
        }
      }

      this.isLoadingValues = false;
    });
  }

  onFieldCreated(event: { fieldName: string; parentId: number | null }) {
    this.isLoading = true;
    this.valuesData = new MatTableDataSource([]);
    this.selectedField = undefined as any;

    this.picklistService
      .createField(
        event.fieldName,
        event.parentId,
        Number(this.currentUser?.facilityId) || 1
      )
      .subscribe(() => {
        this.showAddFieldForm = false;
        this.loadFieldsData();
      });
  }

  onValueCreated(data: IPicklistsValuesCreateData) {
    this.isLoadingValues = true;

    // Split input by newlines to get multiple values
    const valueArray = data.value.split("\n");

    this.picklistService
      .createMultipleValues(
        valueArray,
        this.selectedField.id,
        data.parentId,
        data.isOnPrem,
        Number(this.currentUser?.facilityId) || 1
      )
      .subscribe(() => {
        this.onFieldRowSelected(this.selectedField);
      });
  }

  editField(field: IPicklistFieldsData) {
    this.selectedField = field;
    this.showEditFieldForm = true;
    this.showAddFieldForm = false;
  }

  onFieldUpdated(field: IPicklistFieldsData) {
    this.showEditFieldForm = false;
    this.loadFieldsData();
  }

  editValue(value: IPicklistValuesData) {
    this.selectedValue = value;
    this.showEditValueForm = true;
    this.showAddValueForm = false;
  }

  onValueUpdated(value: IPicklistValuesData) {
    this.showEditValueForm = false;
    this.picklistService.updateValue(value).subscribe(() => {
      this.onFieldRowSelected(this.selectedField);
    });
  }

  confirmDeleteField(field: IPicklistFieldsData) {
    if (confirm("Are you sure you want to delete this field?")) {
      this.isLoading = true;
      this.picklistService.deleteField(field.id).subscribe(() => {
        this.loadFieldsData();
      });
    }
  }

  confirmDeleteValue(value: IPicklistValuesData) {
    if (confirm(`Are you sure you want to delete value: ${value.value}?`)) {
      this.isLoadingValues = true;
      const fieldId = this.selectedField.id;
      this.picklistService
        .deleteValue(fieldId, value)
        .subscribe(() => this.onFieldRowSelected(this.selectedField));
    }
  }

  sortFieldsChange(event: any): void {
    if (this.fieldsData) {
      const { active, direction } = event;
      this.fieldsData.sort.active = active;
      this.fieldsData.sort.direction = direction;
    }
  }

  sortValuesChange(event: any): void {
    if (this.valuesData) {
      const { active, direction } = event;
      this.valuesData.sort.active = active;
      this.valuesData.sort.direction = direction;
    }
  }
}
